// DOC-DEPS: LLM.md -> docs/CURRENT_TASK.md -> src/store/useAuthStore.ts -> src/types/userProfile.ts
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { requestNotificationPermission } from '../../services/notifications/localNotificationService';
import { Mail, ChevronRight, Lock, Loader2, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { callActivateTrialAPI } from '../../api/client';
import { useAuthStore } from '../../store/useAuthStore';
import { useResendCodeCooldown } from '../auth/useResendCodeCooldown';
import {
  getAuthErrorMessage,
  isDuplicateEmailError,
  isObfuscatedDuplicateSignUp,
} from '../auth/authSignupHelpers';
import { useTodoStore } from '../../store/useTodoStore';
import { useGrowthStore } from '../../store/useGrowthStore';
import { saveLocalOnboardingCompleted } from '../../store/authProfileHelpers';
import { OnboardingStepRoutine, type RoutineState } from './OnboardingStepRoutine';
import {
  DEFAULT_WAKE_TIME, DEFAULT_SLEEP_TIME,
  DEFAULT_BREAKFAST, DEFAULT_LUNCH, DEFAULT_DINNER,
  toHour,
} from '../profile/components/userProfilePanelHelpers';
import type { UserProfileManualV2, ClassSchedule } from '../../types/userProfile';
import profileVanAvatar from '../../assets/profile-ai-companions/van.png';
import profileAgnesAvatar from '../../assets/profile-ai-companions/agnes.png';
import profileZepAvatar from '../../assets/profile-ai-companions/zep.png';
import profileMomoAvatar from '../../assets/profile-ai-companions/momo.png';
import dailyJournalIllustration from '../../assets/onboarding/trial-features/daily-journal.png';
import magicPenIllustration from '../../assets/onboarding/trial-features/magic-pen.png';
import taskBreakdownIllustration from '../../assets/onboarding/trial-features/task-breakdown.png';
import moodRecognitionIllustration from '../../assets/onboarding/trial-features/mood-recognition.png';
import goalTrackingIllustration from '../../assets/onboarding/trial-features/goal-tracking.png';
import personalMemoryIllustration from '../../assets/onboarding/trial-features/personal-memory.png';
import greenhouseCompanionsIllustration from '../../assets/onboarding/trial-features/greenhouse-companions.png';
import { StepTodo, type OnboardingTodoDraft } from './components/StepTodo';
import { StepBottle, type OnboardingBottleDraft } from './components/StepBottle';
import { PrivacyPolicyPanel } from '../profile/components/PrivacyPolicyPanel';
import { TermsPanel } from '../profile/components/TermsPanel';
import { StepJournal } from './components/StepJournal';
import {
  AI_COMPANION_ORDER,
  AI_COMPANION_VISUALS,
} from '../../constants/aiCompanionVisuals';
import type { AiCompanionMode } from '../../lib/aiCompanion';

const TOTAL_STEPS = 7;
function toDueAtFromTime(time: string): number | undefined {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return undefined;
  const date = new Date();
  date.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return date.getTime();
}

function buildClassSchedule(start: string, end: string): ClassSchedule | undefined {
  if (!start || !end) return undefined;
  return {
    weekdays: [1, 2, 3, 4, 5],
    morning: { start, end },
  };
}

const ProgressBar: React.FC<{ step: number }> = ({ step }) => (
  <div className="flex gap-1.5 px-8 pt-safe pt-6 pb-2 shrink-0">
    {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
      <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i < step ? 'bg-[#4a5d4c]' : 'bg-[#4a5d4c]/15'}`} />
    ))}
  </div>
);

// ── StepAuth ──────────────────────────────────────────────────
const StepAuth: React.FC<{ onNext: () => void }> = ({ onNext }) => {
  const authMascotSrc = '/assets/auth-login-mascot.png';
  const { t } = useTranslation();
  const { signIn, signUp, verifySignUpCode, resendSignUpCode, signInWithApple, signInWithGoogle } = useAuthStore();
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [nickname, setNickname] = React.useState('');
  const [verificationCode, setVerificationCode] = React.useState('');
  const [pendingSignUpEmail, setPendingSignUpEmail] = React.useState<string | null>(null);
  const [isLogin, setIsLogin] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [googleLoading, setGoogleLoading] = React.useState(false);
  const [appleLoading, setAppleLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [showPrivacy, setShowPrivacy] = React.useState(false);
  const [showTerms, setShowTerms] = React.useState(false);
  const resendCooldown = useResendCodeCooldown();

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const resetSignUpCodeState = () => {
    setVerificationCode('');
    setPendingSignUpEmail(null);
    resendCooldown.reset();
  };

  const handleResend = async () => {
    if (!pendingSignUpEmail || resendCooldown.isCoolingDown) return;
    setResendLoading(true);
    setError(null);
    try {
      const { error: resendError } = await resendSignUpCode(pendingSignUpEmail);
      if (resendError) throw resendError;
      resendCooldown.start();
    } catch (err: any) {
      setError(getAuthErrorMessage(t, err.message || t('auth_error_generic')));
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const acc = identifier.trim();
      if (!isValidEmail(acc)) throw new Error(t('auth_error_invalid_account'));
      const emailToUse = acc;
      if (isLogin) {
        const { error: err } = await signIn(emailToUse, password);
        if (err) throw err;
        onNext();
      } else {
        if (pendingSignUpEmail) {
          const { error: verifyError } = await verifySignUpCode(pendingSignUpEmail, verificationCode);
          if (verifyError) throw verifyError;
          resetSignUpCodeState();
          onNext();
        } else {
          const { data: signUpData, error: err } = await signUp(emailToUse, password, nickname || undefined);
          if (isDuplicateEmailError(err) || isObfuscatedDuplicateSignUp({
            email: emailToUse,
            data: signUpData,
            error: err,
          })) {
            throw new Error(t('auth_error_user_exists'));
          }
          if (err) throw err;
          setPendingSignUpEmail(emailToUse);
          resendCooldown.start();
        }
      }
    } catch (err: any) {
      setError(getAuthErrorMessage(t, err.message || t('auth_error_generic')));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setAppleLoading(true);
    setError(null);
    const { error: err } = await signInWithApple();
    if (err) {
      setError(getAuthErrorMessage(t, err.message || t('auth_error_generic')));
      setAppleLoading(false);
    } else {
      onNext();
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);
    const { error: err } = await signInWithGoogle();
    if (err) {
      setError(getAuthErrorMessage(t, err.message || t('auth_error_generic')));
      setGoogleLoading(false);
    }
  };

  const canSubmit = isLogin
    ? Boolean(identifier.trim() && password.length >= 6 && !loading)
    : pendingSignUpEmail
      ? Boolean(verificationCode.trim().length === 6 && !loading)
      : Boolean(identifier.trim() && password.length >= 6 && !loading);

  if (showTerms) {
    return <TermsPanel onClose={() => setShowTerms(false)} />;
  }
  if (showPrivacy) {
    return <PrivacyPolicyPanel onClose={() => setShowPrivacy(false)} />;
  }

  return (
    <div className="flex-1 flex flex-col px-8 pt-16 pb-12 bg-[#f4f7f4]">
      <div className="mb-8">
        <motion.img
          src={authMascotSrc}
          alt=""
          aria-hidden="true"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mb-8 h-24 w-24 object-contain"
        />

        <h2 className="app-display-title text-[#4a5d4c]">
          {isLogin ? t('auth_welcome_back') : t('auth_create_account')}
        </h2>

        <p className="app-body mt-4 text-[#4a5d4c]/60">
          {isLogin ? t('auth_login_subtitle') : t('auth_register_subtitle')}
        </p>
      </div>

      <div className="space-y-3">
        {/* 账号输入 */}
        <div className="bg-white/60 backdrop-blur-xl border border-white p-5 rounded-[24px] shadow-sm flex items-center gap-3 group focus-within:border-[#8fae91] focus-within:bg-white transition-all">
          <div className="text-[#4a5d4c]/30 group-focus-within:text-[#4a5d4c] transition-colors">
            <Mail size={20} />
          </div>
          <input
            type="text"
            value={identifier}
            onChange={(e) => {
              setIdentifier(e.target.value);
              if (!isLogin) {
                resetSignUpCodeState();
                setMessage(null);
              }
            }}
            placeholder={t('auth_account_placeholder')}
                  className="app-form-text flex-1 bg-transparent border-none outline-none text-[#4a5d4c] font-bold placeholder:text-[#4a5d4c]/20"
          />
        </div>

        {/* 昵称（仅注册） */}
        {!isLogin && !pendingSignUpEmail && (
          <div className="bg-white/60 backdrop-blur-xl border border-white p-5 rounded-[24px] shadow-sm flex items-center gap-3 group focus-within:border-[#8fae91] focus-within:bg-white transition-all">
            <div className="text-[#4a5d4c]/30 group-focus-within:text-[#4a5d4c] transition-colors">
              <User size={20} />
            </div>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={t('auth_nickname_placeholder')}
                  className="app-form-text flex-1 bg-transparent border-none outline-none text-[#4a5d4c] font-bold placeholder:text-[#4a5d4c]/20"
            />
          </div>
        )}

        {pendingSignUpEmail ? (
          <div className="rounded-[24px] border border-[#8fae91]/40 bg-[#eef6ef] px-4 py-4 text-[#4a5d4c] shadow-sm">
            <p className="app-description font-bold">{t('auth_register_success')}</p>
            <p className="app-body mt-2 break-all font-black">{pendingSignUpEmail}</p>
          </div>
        ) : null}

        {pendingSignUpEmail ? (
          <div className="bg-white/60 backdrop-blur-xl border border-white p-5 rounded-[24px] shadow-sm flex items-center gap-3 group focus-within:border-[#8fae91] focus-within:bg-white transition-all">
            <div className="text-[#4a5d4c]/30 group-focus-within:text-[#4a5d4c] transition-colors">
              <Lock size={20} />
            </div>
            <input
              type="text"
              inputMode="numeric"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && canSubmit && handleSubmit()}
              placeholder={t('auth_otp_placeholder')}
                  className="app-form-text flex-1 bg-transparent border-none outline-none text-[#4a5d4c] font-bold placeholder:text-[#4a5d4c]/20"
            />
          </div>
        ) : (
          <div className="bg-white/60 backdrop-blur-xl border border-white p-5 rounded-[24px] shadow-sm flex items-center gap-3 group focus-within:border-[#8fae91] focus-within:bg-white transition-all">
            <div className="text-[#4a5d4c]/30 group-focus-within:text-[#4a5d4c] transition-colors">
              <Lock size={20} />
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && canSubmit && handleSubmit()}
              placeholder={t('onboarding2_auth_password_placeholder')}
                  className="app-form-text flex-1 bg-transparent border-none outline-none text-[#4a5d4c] font-bold placeholder:text-[#4a5d4c]/20"
            />
          </div>
        )}

        {pendingSignUpEmail ? (
          <div className="flex justify-end px-1">
            <button
              type="button"
              onClick={() => { void handleResend(); }}
              disabled={resendLoading || resendCooldown.isCoolingDown}
              className="app-description font-bold text-[#4a5d4c]/50 underline decoration-[#4a5d4c]/20 disabled:opacity-40"
            >
              {resendLoading ? <Loader2 size={12} className="mr-1 inline animate-spin" /> : null}
              {t('auth_resend_code')}
              {resendCooldown.isCoolingDown ? ` (${resendCooldown.remainingSeconds}s)` : null}
            </button>
          </div>
        ) : null}

        {/* 错误 / 成功提示 */}
        {error && <p className="app-description px-2 text-red-500">{error}</p>}
        {message && !pendingSignUpEmail && <p className="app-description px-2 text-[#4a5d4c]">{message}</p>}
        {/* 切换登录/注册 */}
        <p className="app-description pt-1 text-center text-[#4a5d4c]/40">
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError(null);
              setMessage(null);
              resetSignUpCodeState();
            }}
            className="text-[#4a5d4c] font-bold underline decoration-[#4a5d4c]/20 ml-1"
          >
            {isLogin ? t('auth_switch_to_register') : t('auth_switch_to_login')}
          </button>
        </p>

        <div className="flex items-center gap-4 py-1">
          <div className="flex-1 h-[1px] bg-[#4a5d4c]/5" />
          <span className="app-badge uppercase text-[#4a5d4c]/20">{t('auth_or_divider')}</span>
          <div className="flex-1 h-[1px] bg-[#4a5d4c]/5" />
        </div>

        <div className="flex gap-4">
          <AuthButton
            icon={appleLoading ? <Loader2 size={20} className="animate-spin" /> : null}
            text={t('auth_apple_signin')}
            className="flex-1"
            onClick={handleAppleSignIn}
            disabled={appleLoading || googleLoading}
          />
          <AuthButton
            icon={googleLoading ? <Loader2 size={20} className="animate-spin" /> : null}
            text={t('auth_google_signin')}
            className="flex-1"
            onClick={handleGoogleSignIn}
            disabled={appleLoading || googleLoading}
          />
        </div>
      </div>

      <div className="mt-auto pt-6">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`app-section-title w-full rounded-[28px] py-5 font-bold shadow-xl transition-all flex items-center justify-center gap-2 ${
            canSubmit
              ? 'bg-[#4a5d4c] text-white shadow-[#4a5d4c]/20 hover:bg-[#3d4d3f]'
              : 'bg-[#4a5d4c]/10 text-[#4a5d4c]/20 shadow-none cursor-not-allowed'
          }`}>
          {loading
            ? <Loader2 size={20} className="animate-spin" />
            : <>{isLogin ? t('auth_login_button') : pendingSignUpEmail ? t('auth_verify_button') : t('auth_register_button')} <ChevronRight size={20} /></>
          }
        </motion.button>

        <p className="app-badge mt-6 text-center font-normal normal-case text-[#4a5d4c]/30">
          {t('auth_agreement_prefix')}{' '}
          <button type="button" onClick={() => setShowTerms(true)} className="underline underline-offset-2 active:opacity-50">
            {t('auth_agreement_terms')}
          </button>
          {' '}{t('auth_agreement_and')}{' '}
          <button type="button" onClick={() => setShowPrivacy(true)} className="underline underline-offset-2 active:opacity-50">
            {t('auth_agreement_privacy')}
          </button>
        </p>
      </div>
    </div>
  );
};

function AuthButton({ icon, text, className = '', onClick, disabled = false }: {
  icon?: React.ReactNode;
  text: string;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <motion.button
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      disabled={disabled}
      className={`bg-white/60 backdrop-blur-xl border border-white p-5 rounded-[24px] flex items-center justify-center gap-3 text-[#4a5d4c] font-bold shadow-sm transition-all hover:bg-white hover:shadow-md disabled:opacity-50 ${className}`}
    >
      {icon} <span className="app-body">{text}</span>
    </motion.button>
  );
}

// ── StepTrialIntro ────────────────────────────────────────────
const TRIAL_FEATURES = [
  { imageSrc: dailyJournalIllustration, titleKey: 'onboarding_trial_feat1_title', descKey: 'onboarding_trial_feat1_desc' },
  { imageSrc: magicPenIllustration, titleKey: 'onboarding_trial_feat2_title', descKey: 'onboarding_trial_feat2_desc' },
  { imageSrc: taskBreakdownIllustration, titleKey: 'onboarding_trial_feat3_title', descKey: 'onboarding_trial_feat3_desc' },
  { imageSrc: moodRecognitionIllustration, titleKey: 'onboarding_trial_feat4_title', descKey: 'onboarding_trial_feat4_desc' },
  { imageSrc: goalTrackingIllustration, titleKey: 'onboarding_trial_feat5_title', descKey: 'onboarding_trial_feat5_desc' },
  { imageSrc: personalMemoryIllustration, titleKey: 'onboarding_trial_feat6_title', descKey: 'onboarding_trial_feat6_desc' },
  { imageSrc: greenhouseCompanionsIllustration, titleKey: 'onboarding_trial_feat7_title', descKey: 'onboarding_trial_feat7_desc' },
] as const;

const StepTrialIntro: React.FC<{ onNext: () => void; preview?: boolean }> = ({ onNext, preview }) => {
  const { t } = useTranslation();
  const [activating, setActivating] = React.useState(false);

  const handleStartExperience = async () => {
    if (activating || preview) return;
    setActivating(true);
    try {
      await callActivateTrialAPI();
    } catch {
      // no-op: keep onboarding flow unblocked if trial activation request fails
    } finally {
      setActivating(false);
      onNext();
    }
  };

  return (
    <div className="flex-1 flex flex-col pb-8 overflow-hidden">
      <div className="px-8 pt-10 pb-4 shrink-0">
        <h2 className="app-page-title font-black text-[#4a5d4c]">{t('onboarding_trial_title')}</h2>
        <p className="app-body mt-2 text-[#4a5d4c]/60">{t('onboarding_trial_subtitle')}</p>
      </div>
      <div className="flex-1 overflow-y-auto px-8 space-y-3 pb-2">
        {TRIAL_FEATURES.map((feat) => (
          <div key={feat.titleKey} className="flex items-start gap-3 bg-white/60 backdrop-blur-xl border border-white rounded-[20px] p-4">
            <img
              src={feat.imageSrc}
              alt=""
              aria-hidden="true"
              className={`w-12 h-12 shrink-0 object-contain ${
                feat.titleKey === 'onboarding_trial_feat6_title'
                  ? '-translate-x-1 translate-y-2'
                  : ''
              }`}
            />
            <div className="min-w-0">
              <p className="app-body font-bold text-[#4a5d4c]">{t(feat.titleKey)}</p>
              <p className="app-description mt-0.5 text-[#4a5d4c]/55">{t(feat.descKey)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="px-8 pt-4 shrink-0">
        <button
          onClick={() => { void handleStartExperience(); }}
          disabled={activating}
          className="app-section-title w-full rounded-[28px] bg-[#4a5d4c] py-5 font-bold text-white shadow-xl shadow-[#4a5d4c]/20"
        >
          {t('onboarding_trial_cta')}
        </button>
        <p className="app-badge mt-3 px-4 text-center text-[#4a5d4c]/30">
          {t('onboarding_trial_footer')}
        </p>
      </div>
    </div>
  );
};

// ── StepAI ────────────────────────────────────────────────────
const StepAI: React.FC<{ onNext: () => void }> = ({ onNext }) => {
  const { t } = useTranslation();
  const { updatePreferences } = useAuthStore();
  const [selectedMode, setSelectedMode] = React.useState<AiCompanionMode>('van');

  const onboardingAvatars: Record<AiCompanionMode, string> = {
    van: profileVanAvatar,
    agnes: profileAgnesAvatar,
    zep: profileZepAvatar,
    momo: profileMomoAvatar,
  };

  const subtitleKeyMap: Record<AiCompanionMode, string> = {
    van: 'profile_ai_mode_van_subtitle',
    agnes: 'profile_ai_mode_agnes_subtitle',
    zep: 'profile_ai_mode_zep_subtitle',
    momo: 'profile_ai_mode_momo_subtitle',
  };

  const handleSelect = () => {
    void updatePreferences({ aiMode: selectedMode, aiModeEnabled: true });
    onNext();
  };

  return (
    <div className="flex-1 flex flex-col px-8 pt-10 pb-12">
      <div className="mb-8 text-center">
        <h2 className="app-page-title font-black text-[#4a5d4c]">{t('onboarding2_ai_title')}</h2>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {AI_COMPANION_ORDER.map((modeKey) => {
          const mode = AI_COMPANION_VISUALS[modeKey];
          const selected = selectedMode === modeKey;
          const badgeKey = mode.free ? 'onboarding2_ai_van_badge' : 'onboarding2_ai_agnes_badge';
          return (
            <button
              key={modeKey}
              type="button"
              onClick={() => setSelectedMode(modeKey)}
              className={`relative flex flex-col items-center p-5 rounded-[28px] border text-center transition-all ${
                selected
                  ? 'bg-[#4a5d4c] border-[#4a5d4c] shadow-2xl'
                  : 'bg-white/60 backdrop-blur-xl border-white hover:bg-white'
              }`}
            >
              <img
                src={onboardingAvatars[modeKey]}
                className="w-24 h-24 object-contain"
                style={{ filter: 'saturate(1.4) drop-shadow(0 2px 8px rgba(0,0,0,0.12))' }}
                alt={mode.name}
              />
              <div className="mt-3">
                <h4 className={`text-lg font-black ${selected ? 'text-white' : 'text-[#4a5d4c]'}`}>
                  {mode.name}
                </h4>
                <p className={`app-description mt-1 ${selected ? 'text-white/70' : 'text-[#4a5d4c]/55'}`}>
                  {t(subtitleKeyMap[modeKey])}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <button onClick={handleSelect} className="app-section-title mt-auto w-full rounded-[28px] bg-[#4a5d4c] py-5 pt-6 font-bold text-white shadow-xl shadow-[#4a5d4c]/20">
        {t('onboarding2_ai_cta')}
      </button>
    </div>
  );
};

// ── Main OnboardingFlow ───────────────────────────────────────
interface OnboardingFlowProps {
  initialStep?: number;
  preview?: boolean;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  initialStep = 1,
  preview = false,
}) => {
  const { user, updateUserProfile, updateAccountState, userProfileV2 } = useAuthStore();
  const addTodo = useTodoStore((state) => state.addTodo);
  const addBottle = useGrowthStore((state) => state.addBottle);
  const navigate = useNavigate();

  const [step, setStep] = React.useState(initialStep);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (preview) return;
    if (user && step === 1) setStep(2);
  }, [preview, user]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (preview) return;
    if (!user?.id || step < 2) return;
    const nowIso = new Date().toISOString();
    void updateAccountState((prev) => ({
      ...(prev || {
        accountStatus: 'active',
        onboardingStatus: 'required',
        onboardingReentryAllowed: false,
        planSnapshot: 'free',
        planSource: 'default_free',
        deletionStatus: 'none',
        createdAt: nowIso,
        updatedAt: nowIso,
      }),
      onboardingStatus: prev?.onboardingStatus === 'completed' ? 'completed' : 'in_progress',
      onboardingLastStep: step,
      onboardingVersion: 'v2_route_flow',
      onboardingStartedAt: prev?.onboardingStartedAt || nowIso,
      onboardingUpdatedAt: nowIso,
      updatedAt: nowIso,
    }));
  }, [preview, step, updateAccountState, user?.id]);

  const [routine, setRoutine] = React.useState<RoutineState>({
    region: '',
    identity: 'none',
    remindMe: true,
    wakeTime: DEFAULT_WAKE_TIME,
    sleepTime: DEFAULT_SLEEP_TIME,
    breakfastTime: DEFAULT_BREAKFAST,
    lunchTime: DEFAULT_LUNCH,
    dinnerTime: DEFAULT_DINNER,
    workStart: '09:00',
    workEnd: '18:00',
    classStart: '08:30',
    classEnd: '17:30',
  });

  const handleRoutineChange = <K extends keyof RoutineState>(key: K, val: RoutineState[K]) =>
    setRoutine((prev) => ({ ...prev, [key]: val }));

  const handleRequestNotificationPermission = async () => {
    if (Capacitor.isNativePlatform()) {
      await requestNotificationPermission();
    }
  };

  const saveRoutineAndProceed = async () => {
    setSaving(true);
    const mealHours = [routine.breakfastTime, routine.lunchTime, routine.dinnerTime]
      .map(toHour).filter((h): h is number => h !== null);
    const hasWork = routine.identity === 'work';
    const hasClass = routine.identity === 'class';
    const manual: UserProfileManualV2 = {
      ...(userProfileV2?.manual || {}),
      wakeTime: routine.wakeTime || undefined,
      sleepTime: routine.sleepTime || undefined,
      mealTimes: mealHours.length ? mealHours : undefined,
      mealTimesText: [routine.breakfastTime, routine.lunchTime, routine.dinnerTime],
      lunchTime: routine.lunchTime || undefined,
      dinnerTime: routine.dinnerTime || undefined,
      hasWorkSchedule: hasWork,
      hasClassSchedule: hasClass,
      workStart: hasWork ? (routine.workStart || undefined) : undefined,
      workEnd: hasWork ? (routine.workEnd || undefined) : undefined,
      lunchStart: undefined,
      lunchEnd: undefined,
      classSchedule: hasClass ? buildClassSchedule(routine.classStart, routine.classEnd) : undefined,
      reminderEnabled: routine.remindMe,
    };
    void updateUserProfile({ manual });
    await handleRequestNotificationPermission();
    setSaving(false);
    handleComplete();
  };

  const handleComplete = () => {
    const nowIso = new Date().toISOString();
    if (user?.id) {
      saveLocalOnboardingCompleted(user.id);
    }
    void updateAccountState((prev) => ({
      ...(prev || {
        accountStatus: 'active',
        onboardingReentryAllowed: false,
        planSnapshot: 'free',
        planSource: 'default_free',
        deletionStatus: 'none',
        createdAt: nowIso,
        updatedAt: nowIso,
      }),
      onboardingStatus: 'completed',
      onboardingCompletedAt: nowIso,
      onboardingLastStep: TOTAL_STEPS,
      onboardingVersion: 'v2_route_flow',
      onboardingStartedAt: prev?.onboardingStartedAt || nowIso,
      onboardingUpdatedAt: nowIso,
      updatedAt: nowIso,
    }));
    void updateUserProfile({ onboardingCompleted: true });
    navigate('/chat', { replace: true });
  };

  const handleTodoNext = (todos: OnboardingTodoDraft[]) => {
    todos.forEach((todo) => {
      addTodo({
        title: todo.text,
        priority: todo.urgency,
        recurrence: todo.repeat ? 'daily' : 'once',
        dueAt: toDueAtFromTime(todo.time),
      });
    });
    next();
  };

  const handleBottleNext = (bottles: OnboardingBottleDraft[]) => {
    bottles.forEach((bottle) => {
      addBottle(bottle.name, bottle.type);
    });
    next();
  };

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'linear-gradient(160deg, #eef3ef 0%, #e6ede7 100%)' }}>
        <ProgressBar step={step} />
      <div className="flex-1 overflow-hidden flex flex-col">
        {step === 1 && <StepAuth onNext={next} />}
        {step === 2 && <StepTrialIntro onNext={next} preview={preview} />}
        {step === 3 && <StepAI onNext={next} />}
        {step === 4 && <StepJournal onNext={next} />}
        {step === 5 && <StepTodo onNext={handleTodoNext} />}
        {step === 6 && <StepBottle onNext={handleBottleNext} />}
        {step === 7 && (
          <OnboardingStepRoutine
            state={routine}
            onChange={handleRoutineChange}
            onNext={() => { void saveRoutineAndProceed(); }}
            saving={saving}
          />
        )}

      </div>
    </div>
  );
};
